import { useCertificates } from '@/hooks/useData'
import ImageModal from '../components/ImageModal'
import { useMemo, useState } from 'react'

const formatDate = (value?: string | number | null) => {
  if (!value) return ''
  const timestamp = typeof value === 'number' ? (value < 1e12 ? value * 1000 : value) : value
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return String(value)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

export default function Qualifications() {
  const { data: certificates = [] } = useCertificates()
  const [selectedImage, setSelectedImage] = useState<{ url: string; alt: string } | null>(null)

  const normalizedCertificates = useMemo(() => {
    const list = certificates.length ? certificates : []
    return list.map((certificate) => ({
      id: String(certificate.id ?? Math.random()),
      name: certificate.name ?? '资质证书',
      imageUrl: certificate.imageUrl || '/images/certificates/qualification-certificate.png',
      description: certificate.description ?? '',
      issueDate: certificate.issueDate ? formatDate(certificate.issueDate) : '',
      validUntil: certificate.validUntil ? formatDate(certificate.validUntil) : '长期有效',
    }))
  }, [certificates])

  const openImageModal = (url: string, alt: string) => {
    setSelectedImage({ url, alt })
  }

  const closeImageModal = () => {
    setSelectedImage(null)
  }
  return (
    <div className="min-h-screen bg-white">
      {/* 资质证书 */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title">资质证书</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {normalizedCertificates.length === 0 ? (
              <div className="col-span-full text-center text-secondary-500">暂无资质证书数据。</div>
            ) : (
              normalizedCertificates.map((certificate) => (
                <div key={certificate.id} className="card">
                  <img
                    src={certificate.imageUrl}
                    alt={certificate.name}
                    className="w-full h-48 object-cover rounded-lg mb-4 cursor-pointer"
                    onClick={() => openImageModal(certificate.imageUrl, certificate.name)}
                  />
                  <h3 className="text-lg font-semibold mb-2">{certificate.name}</h3>
                  {certificate.description && (
                    <p className="text-secondary-600 mb-3 leading-relaxed">{certificate.description}</p>
                  )}
                  <div className="text-xs text-secondary-500 space-y-1">
                    {certificate.issueDate && <p>签发日期：{certificate.issueDate}</p>}
                    {certificate.validUntil && <p>有效期至：{certificate.validUntil}</p>}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* 技术能力 */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title">技术能力</h2>
            <p className="section-subtitle">
              拥有先进的检测设备和专业的技术团队
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card text-center">
              <div className="text-4xl mb-4">🔬</div>
              <h3 className="text-lg font-semibold mb-2">检测设备</h3>
              <p className="text-secondary-600">
                配备先进的LDAR检测设备，检测精度达到ppm级别
              </p>
            </div>
            <div className="card text-center">
              <div className="text-4xl mb-4">💻</div>
              <h3 className="text-lg font-semibold mb-2">管理平台</h3>
              <p className="text-secondary-600">
                自主研发LDAR管理平台，实现数据智能化管理
              </p>
            </div>
            <div className="card text-center">
              <div className="text-4xl mb-4">👥</div>
              <h3 className="text-lg font-semibold mb-2">专业团队</h3>
              <p className="text-secondary-600">
                拥有经验丰富的环保技术专家和工程师团队
              </p>
            </div>
            <div className="card text-center">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-lg font-semibold mb-2">数据分析</h3>
              <p className="text-secondary-600">
                提供专业的数据分析和报告服务
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 服务优势 */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title">服务优势</h2>
            <p className="section-subtitle">
              为客户提供专业、高效、可靠的环保技术服务
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-semibold mb-6">专业服务</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center mr-3 mt-1">✓</div>
                  <div>
                    <h4 className="font-semibold">技术专业</h4>
                    <p className="text-secondary-600">拥有专业的环保技术团队，具备丰富的项目经验</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center mr-3 mt-1">✓</div>
                  <div>
                    <h4 className="font-semibold">设备先进</h4>
                    <p className="text-secondary-600">配备先进的检测设备，确保检测精度和效率</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center mr-3 mt-1">✓</div>
                  <div>
                    <h4 className="font-semibold">服务完善</h4>
                    <p className="text-secondary-600">提供从检测到修复的全流程服务</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-semibold mb-6">质量保证</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center mr-3 mt-1">✓</div>
                  <div>
                    <h4 className="font-semibold">资质齐全</h4>
                    <p className="text-secondary-600">具备多项管理体系认证，确保服务质量</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center mr-3 mt-1">✓</div>
                  <div>
                    <h4 className="font-semibold">数据准确</h4>
                    <p className="text-secondary-600">采用标准检测方法，确保数据准确可靠</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center mr-3 mt-1">✓</div>
                  <div>
                    <h4 className="font-semibold">持续改进</h4>
                    <p className="text-secondary-600">建立完善的质量管理体系，持续改进服务质量</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <ImageModal isOpen={!!selectedImage} onClose={closeImageModal} imageUrl={selectedImage?.url || ''} alt={selectedImage?.alt || ''} />
    </div>
  )
} 
